import { z } from "zod";
import { IdSchema, IsoDateSchema, IsoDateTimeSchema } from "./common.js";
import { SHIPMENT_DIRECTIONS } from "../constants/index.js";

/**
 * 진실의 원천: harness/API-SPEC.md §5, harness/RMA-PROCEDURE.md §2 원칙4
 */

export const ShipmentSchema = z.object({
  id: IdSchema,
  case_id: IdSchema,
  direction: z.enum(SHIPMENT_DIRECTIONS),
  dest_company_id: IdSchema,
  shipped_at: IsoDateSchema,
  carrier: z.string().nullable(),
  tracking_raw: z.string().nullable(),
  tracking_norm: z.string().nullable(),
  approval_id: IdSchema,
  created_at: IsoDateTimeSchema,
});
export type Shipment = z.infer<typeof ShipmentSchema>;

export const ShipmentCreateRequestSchema = z.object({
  case_id: IdSchema,
  direction: z.enum(SHIPMENT_DIRECTIONS),
  dest_company_id: IdSchema,
  shipped_at: IsoDateSchema,
  carrier: z.string().optional(),
  /**
   * 송장번호. **없는 출고가 실제로 있다** — 직접 전달한 건이 그렇고, 실측 파일에도
   * 송장 칸이 빈 출고가 있다. 없으면 `null` 로 저장한다 (빈 문자열로 바꾸지 않는다).
   * 서버가 `normalizeTracking()` 으로 raw/norm 을 분리한다.
   */
  tracking: z.string().nullable(),
  approval_id: IdSchema, // 필수 — 없으면 409 (harness/APPROVAL-POLICY.md §2)
});
export type ShipmentCreateRequest = z.infer<typeof ShipmentCreateRequestSchema>;

/** 한 번에 등록할 수 있는 출고 수 — 배치 입고와 같은 이유의 상한 (반증 RT-9) */
export const SHIPMENT_BATCH_MAX = 200;

/**
 * 일괄 출고 (harness/API-SPEC.md §5-2).
 *
 * 실제 업무가 그렇다: 택배 한 묶음을 같은 날 같은 곳으로 보내고, 송장번호만 건마다
 * 다르다. 실측 91행 중 77건이 출고이고 대부분 같은 날·같은 행선지다.
 *
 * `approval_id` 를 **받지 않는다.** 서버가 그 케이스의 방향에 맞는 `APPROVED` 승인을
 * 찾아 쓴다 — 승인 잠금은 그대로다(없으면 그 건은 막힌다). 사람이 승인 번호를
 * 200개 타이핑하게 만드는 것은 잠금이 아니라 벌이다.
 */
export const ShipmentBatchRequestSchema = z.object({
  direction: z.enum(SHIPMENT_DIRECTIONS),
  dest_company_id: IdSchema,
  shipped_at: IsoDateSchema,
  carrier: z.string().optional(),
  items: z
    .array(
      z.object({
        case_id: IdSchema,
        // 송장이 없는 출고도 있다(직접 전달). 빈 값이면 null 로 저장한다 —
        // 없는 것과 빈 문자열은 다르다
        tracking: z.string().nullable(),
      }),
    )
    .min(1)
    .max(SHIPMENT_BATCH_MAX),
});
export type ShipmentBatchRequest = z.infer<typeof ShipmentBatchRequestSchema>;

export const ShipmentBatchResponseSchema = z.object({
  created: z.array(ShipmentSchema),
  // 못 만든 건은 **이유를 말해 준다.** 조용히 빠뜨리면 나간 줄 알고 넘어간다
  blocked: z.array(
    z.object({ case_id: z.number().int(), case_no: z.string().nullable(), reason: z.string() }),
  ),
  /**
   * 출고는 됐지만 날짜가 말이 되지 않는 건. 일괄 출고에서는 각 케이스의 입고일이
   * 화면에 없으므로 **사람이 볼 수 없는 모순**이다 — 입고보다 앞선 출고일이 들어가면
   * 장비 이력이 "나가고 나서 들어왔다" 가 된다(RT-13 이 정렬에서 겪은 그 문제의 원천).
   */
  warnings: z.array(
    z.object({ case_id: z.number().int(), case_no: z.string().nullable(), message: z.string() }),
  ),
});
export type ShipmentBatchResponse = z.infer<typeof ShipmentBatchResponseSchema>;
